import { Color, Euler, IcosahedronGeometry, type Object3D, Quaternion } from "three";
import { createSkillBatchMaterial } from "../render/shaders/skill-mesh.ts";
import { SKILL_SHAPES, type SkillShapeId } from "../render/geometry/skill-shapes.ts";
import { SkillBatch } from "../render/skill-batch.ts";

/** Uma peça 3D de um efeito (F78): onde nasce, como anda e quanto vive. Pura, para os testes. */
export interface PieceSpec {
  shape: SkillShapeId;
  x: number;
  y: number;
  z: number;
  scale: number;
  /** Escala no fim (padrão: a mesma). */
  scaleEnd?: number;
  /** Comprimento no eixo longo da peça (a coluna em Y, o prisma em Z), fora da escala. */
  length?: number;
  yaw?: number;
  /** Inclinação para fora (rad), nas lanças. */
  tilt?: number;
  /** Sobe tanto (m) ao longo da vida, com saída suave. */
  rise?: number;
  /** Gira em volta de (cx, cz), do raio `r0` ao `r1`, a `speed` rad/s a partir do ângulo `a0`. */
  orbit?: { cx: number; cz: number; r0: number; r1: number; a0: number; speed: number };
  delayMs?: number;
  ms: number;
  /** Brilho das arestas (acima de 1 o bloom pega) e o preenchimento da face. */
  glow?: number;
  fill?: number;
}

/** O evento de habilidade que o desenho precisa (o `SkillEvent`, sem quem usou). */
export interface PieceEvent {
  skill: string;
  x: number;
  z: number;
  radius: number;
  ms: number;
  x2?: number;
  z2?: number;
}

const TAU = Math.PI * 2;
/** Os nós do domo: os vértices do icosaedro de cima (guia §18, "nós da rede"). */
const DOME_NODES = (() => {
  const p = new IcosahedronGeometry(1, 0).getAttribute("position");
  const out: [number, number, number][] = [];
  for (let i = 0; i < p.count; i++) {
    const v: [number, number, number] = [p.getX(i), p.getY(i), p.getZ(i)];
    if (v[1] > -0.1 && !out.some((o) => Math.hypot(o[0] - v[0], o[1] - v[1], o[2] - v[2]) < 1e-3)) out.push(v);
  }
  return out;
})();

/** O eixo longo das peças que esticam (`length`). */
const LONG_AXIS: Partial<Record<SkillShapeId, "y" | "z">> = { coluna: "y", prisma: "z" };

/** Penas da Fênix: quanto cada uma vive e de quanto em quanto sai uma. */
const FEATHER_MS = 1600;
const FEATHER_EVERY = 500;

const ring = (n: number, f: (a: number, i: number) => PieceSpec): PieceSpec[] => Array.from({ length: n }, (_, i) => f((i / n) * TAU, i));

/**
 * As peças de cada habilidade (doc 09 §3.4), por dados: o domo com nós do Bastião, as lanças do Salto, os
 * triângulos da Runa girando, os nós e as penas da Fênix, o prisma do Rompe-Núcleo e as facetas do Brado. As
 * outras ficam com a onda ou a zona no chão (`SkillVfx`). `gy`: a altura do chão no centro.
 */
export function skillPieces(e: PieceEvent, gy: number): PieceSpec[] {
  const { x, z, radius: r } = e;
  switch (e.skill) {
    case "bastiao":
      return [
        { shape: "domo", x, y: gy, z, scale: r, ms: e.ms, glow: 1.6, fill: 0.05 },
        ...DOME_NODES.map(([nx, ny, nz]): PieceSpec => ({ shape: "no", x: x + nx * r, y: gy + ny * r, z: z + nz * r, scale: 1.4, ms: e.ms, glow: 3, fill: 0.6 })),
      ];
    case "salto-do-falcao":
      return ring(6, (a) => ({ shape: "lanca", x: x + Math.cos(a) * r * 0.75, y: gy - 0.9, z: z + Math.sin(a) * r * 0.75, scale: 1, yaw: -a + Math.PI / 2, tilt: 0.45, rise: 0.9, ms: 900, glow: 2.6, fill: 0.2 }));
    case "runa-de-contencao":
      return ring(6, (a) => ({ shape: "triangulo", x, y: gy + 0.12, z, scale: 1, orbit: { cx: x, cz: z, r0: r * 0.85, r1: r * 0.85, a0: a, speed: 1.2 }, ms: e.ms, glow: 1.8, fill: 0 }));
    case "circulo-da-fenix":
      return [
        ...ring(8, (a) => ({ shape: "no", x: x + Math.cos(a) * r, y: gy + 0.2, z: z + Math.sin(a) * r, scale: 1.2, ms: e.ms, glow: 2, fill: 0.6 })),
        ...ring(6, (a) => ({ shape: "no", x: x + Math.cos(a + 0.4) * r * 0.75, y: gy + 0.2, z: z + Math.sin(a + 0.4) * r * 0.75, scale: 1, ms: e.ms, glow: 2, fill: 0.6 })),
        // Penas subindo de pontos dentro do círculo, em levas, sem passar do fim da zona.
        ...Array.from({ length: Math.max(1, Math.floor((e.ms - FEATHER_MS) / FEATHER_EVERY) + 1) }, (_, i): PieceSpec => {
          const a = i * 2.39996;
          const d = r * (0.25 + 0.6 * ((i * 0.618) % 1));
          return { shape: "pena", x: x + Math.cos(a) * d, y: gy, z: z + Math.sin(a) * d, scale: 0.8, yaw: a, rise: 2.6, delayMs: i * FEATHER_EVERY, ms: FEATHER_MS, glow: 1.8, fill: 0.15 };
        }),
      ];
    case "disparo-rompe-nucleo": {
      if (e.x2 === undefined || e.z2 === undefined) return [];
      const len = Math.hypot(e.x2 - x, e.z2 - z);
      const n = Math.max(1, Math.round(len / 2.5));
      const yaw = Math.atan2(e.x2 - x, e.z2 - z);
      return Array.from({ length: n }, (_, i): PieceSpec => {
        const t = (i + 0.5) / n;
        return { shape: "prisma", x: x + (e.x2! - x) * t, y: gy + 1.1, z: z + (e.z2! - z) * t, scale: 1, length: len / n, yaw, delayMs: i * 12, ms: 600, glow: 2.8, fill: 0.12 };
      });
    }
    case "brado":
      return ring(6, (a) => ({ shape: "faceta", x, y: gy + 0.3, z, scale: 1, scaleEnd: 1.6, orbit: { cx: x, cz: z, r0: 0.6, r1: r, a0: a, speed: 0 }, ms: 600, glow: 2, fill: 0.1 }));
    default:
      return [];
  }
}

/** A cúpula do Bastião trinca onde o arremesso bateu (F96, doc 09 §3.4): facetas que saltam do ponto. */
export function domeCrack(x: number, y: number, z: number): PieceSpec[] {
  return ring(5, (a) => ({ shape: "faceta", x, y, z, scale: 0.6, scaleEnd: 1, orbit: { cx: x, cz: z, r0: 0.2, r1: 1.4, a0: a, speed: 0 }, ms: 450, glow: 2.4, fill: 0.15 }));
}

/** Coluna de luz de quem voltou da Lápide (doc 09 §3.4: a Lápide revivida vira coluna de luz). */
export function reviveColumn(x: number, y: number, z: number): PieceSpec {
  return { shape: "coluna", x, y, z, scale: 1, length: 8, ms: 1400, glow: 2.4, fill: 0.08 };
}

/** Opacidade ao longo da vida (0 a 1): entra em 10 % e sai nos últimos 30 %. */
export function pieceAlpha(k: number): number {
  if (k < 0 || k >= 1) return 0;
  return Math.min(1, k / 0.1) * Math.min(1, (1 - k) / 0.3);
}

interface Live {
  spec: PieceSpec;
  start: number;
  alpha: number;
  /** Cor linear de quem usou. */
  r: number;
  g: number;
  b: number;
}

/** Teto global das peças decorativas; áreas no chão têm orçamento separado (`SkillVfx`). */
export const SKILL_PIECE_CAPACITY = 128;

const tint = new Color();
const turn = new Euler(0, 0, 0, "YXZ");
const spin = new Quaternion();

/**
 * As peças vivas (F78), na cor de quem usou, em lotes por forma (Fase 12, Gate B): um draw call por
 * forma em uso, um programa para todas (`createSkillBatchMaterial`, o mesmo das fitas e dos orbes).
 * Cada peça é só dado (a forma, o tempo, a cor); a cada `update` os lotes são reescritos com as vivas.
 * Acima do teto, as novas não entram (as vivas terminam o que começaram).
 */
export class SkillPieces {
  private readonly root: Object3D;
  private readonly live: Live[] = [];
  private readonly capacity: number;
  private readonly material = createSkillBatchMaterial(false);
  private readonly batches = new Map<SkillShapeId, SkillBatch>();
  /** "Reduzir lampejos": as arestas sem o pico acima de 1 (o bloom não pega), como as chamas e a fita. */
  reduceFlashes = false;

  constructor(root: Object3D, capacity = SKILL_PIECE_CAPACITY) {
    this.root = root;
    this.capacity = capacity;
  }

  spawn(specs: readonly PieceSpec[], color: string, now: number, alpha = 1): void {
    // "Efeitos dos aliados" em 0 (F68): nada a desenhar.
    if (alpha <= 0) return;
    this.prune(now);
    tint.set(color);
    for (const spec of specs) {
      if (this.live.length >= this.capacity) break;
      this.live.push({ spec, start: now + (spec.delayMs ?? 0), alpha, r: tint.r, g: tint.g, b: tint.b });
    }
  }

  /** O lote da forma, criado no primeiro uso. */
  private batch(shape: SkillShapeId): SkillBatch {
    let batch = this.batches.get(shape);
    if (!batch) {
      batch = new SkillBatch(SKILL_SHAPES[shape], this.material, this.capacity);
      this.batches.set(shape, batch);
      this.root.add(batch.mesh);
    }
    return batch;
  }

  /** Tira as que acabaram (sem desenhar). */
  private prune(now: number): void {
    for (let i = this.live.length - 1; i >= 0; i--) {
      const l = this.live[i]!;
      if ((now - l.start) / l.spec.ms >= 1) this.live.splice(i, 1);
    }
  }

  update(now: number): void {
    for (const batch of this.batches.values()) batch.begin();
    for (let i = this.live.length - 1; i >= 0; i--) {
      const l = this.live[i]!;
      const s = l.spec;
      const k = (now - l.start) / s.ms;
      if (k >= 1) {
        this.live.splice(i, 1);
        continue;
      }
      if (k < 0) continue;
      const ease = 1 - (1 - k) * (1 - k);
      let px = s.x;
      let pz = s.z;
      let yaw = s.yaw ?? 0;
      if (s.orbit) {
        const o = s.orbit;
        const a = o.a0 + o.speed * ((now - l.start) / 1000);
        const rr = o.r0 + (o.r1 - o.r0) * ease;
        px = o.cx + Math.cos(a) * rr;
        pz = o.cz + Math.sin(a) * rr;
        yaw = -a;
      }
      spin.setFromEuler(turn.set(s.tilt ?? 0, yaw, 0, "YXZ"));
      const sc = s.scale + ((s.scaleEnd ?? s.scale) - s.scale) * ease;
      const axis = s.length === undefined ? undefined : (LONG_AXIS[s.shape] ?? "y");
      this.batch(s.shape).push(
        px, s.y + (s.rise ?? 0) * ease, pz,
        spin.x, spin.y, spin.z, spin.w,
        sc, axis === "y" ? s.length! : sc, axis === "z" ? s.length! : sc,
        l.r, l.g, l.b, pieceAlpha(k) * l.alpha,
        this.reduceFlashes ? Math.min(1, s.glow ?? 1.8) : (s.glow ?? 1.8), s.fill ?? 0.04, 0, 0,
      );
    }
    for (const batch of this.batches.values()) batch.end();
  }

  get count(): number {
    return this.live.length;
  }

  /** Lotes criados (um por forma já usada): o teto é o número de formas. */
  get batchCount(): number {
    return this.batches.size;
  }

  dispose(): void {
    for (const batch of this.batches.values()) batch.dispose();
    this.batches.clear();
    this.material.dispose();
    this.live.length = 0;
  }
}
